"use client";;
import { Skeleton } from '@/app/ui/imported/skeleton';
import AnimatedIcon from '@/app/ui/utils/AnimatedIcon';
import { GoogleAnalyticsEvent, youtube_getVideoLink } from '@/lib/utils';
import { sendGAEvent } from '@next/third-parties/google';
import { Video, Youtuber } from '@prisma/client';
import {
    CategoryScale,
    ChartData,
    Chart as ChartJS,
    ChartOptions,
    Colors,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    TimeScale,
    Tooltip
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
ChartJS.register(LineElement, PointElement, Tooltip, Legend, CategoryScale, LinearScale, TimeScale, Colors, zoomPlugin);

type YoutuberFull = Youtuber & {
    videos: Video[];
};

export default function GraphComponent({ youtubers }: { youtubers: YoutuberFull[] }) {
    const [noContent, setNoContent] = useState(false)
    //memorize all the configurations & datasets
    const chartJsCfg = useMemo(() => {
        const { youtubers_formattedData, flatData, uniqueDates } = ProcessData(youtubers)
        if (flatData.length == 0)
            setNoContent(true)
        else
            setNoContent(false)
        const options = CreateChartOptions(youtubers, uniqueDates, flatData)
        const data = CreateChartData(youtubers, uniqueDates, youtubers_formattedData);
        return {
            options,
            data
        };
    }, [youtubers]);

    useEffect(() => {
        GoogleAnalyticsEvent({ event_name: "openStatistics", "event_label": youtubers.map(y => y.name).sort().join("/") });
    }, [youtubers])

    return (
        <div className='w-full h-60 sm:h-72 landscape:h-[min(55vmin,288px)] relative'>
            { noContent &&
                <div className='inset-0 m-auto w-fit h-min text-4xl absolute'>
                    <AnimatedIcon icon={ "error" } className="text-9xl" play noHover />
                    אין עדיין מידע להציג..
                </div>
                ||
                <Line data={ chartJsCfg.data } options={ chartJsCfg.options } />
            }
        </div>
    );
}

export function GraphComponent_Skeleton() {
    return <Skeleton className='w-full h-60 sm:h-72 landscape:h-[min(55vmin,288px)]'></Skeleton>
}


/**
 *  HELPER FUNCTION
 */

/** helper function to group logic for data processing */
function ProcessData(youtubers: YoutuberFull[]) {
    /** an array of youtubers with video listing */
    const youtubers_formattedData = youtubers.map(yt => {
        const videoMap = yt.videos
            .map(a => a)//create copy
            .reverse() //reverse order (ltr in graph view)
            .map((video, index, arr) => { //formate data
                /**
                * Format video data:
                * - Calculate cumulative views up to this video
                * - Convert `createdAt` to a `Date` object for compatibility
                */
                const viewsSum_at_point = arr.slice(0, index + 1).reduce((total, current) => total + current.views, 0);
                return {
                    ...video,
                    createdAt: new Date(video.createdAt),
                    viewsSum_at_point,
                };
            });
        return {
            ...yt,
            videos: videoMap
        }

    })

    /** big array of all videos from all youtubers */
    const flatData = youtubers_formattedData
        .map(yt => yt.videos) //get videos only
        .reduce((a, b) => ([...a, ...b])) //sum to a single array of all videos
        .sort((a, b) => a.createdAt > b.createdAt ? 1 : -1) //sort by date

    /** all the dates a video has been posted */
    const uniqueDates = Array.from(new Set(flatData.map(a => a.createdAt)));

    return { youtubers_formattedData, flatData, uniqueDates }
}

/** helper function to group logic for data processing */
function CreateChartOptions(youtubers: YoutuberFull[], uniqueDates: Date[], flatData: Video[]): ChartOptions<"line"> {
    /** oldest video from all youtubers */
    const oldestVideoDate = uniqueDates[0] || new Date()
    /** newest video from all youtubers */
    const newestVideoDate = uniqueDates[uniqueDates.length - 1] || new Date();



    return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: GenerateXAxisOptions(oldestVideoDate.getTime(), newestVideoDate.getTime()),
            y: {
                ...GenerateAxis({ title: { text: 'צפיות לפרק' } }),
                position: 'left', // Primary y-axis for Views per Video
            },
            y1: {
                ...GenerateAxis({ title: { text: 'סך הצפיות' } }),
                position: 'right', // Secondary y-axis for Cumulative Views
                grid: {
                    drawOnChartArea: false, // Prevent overlapping grid lines
                },
                display: youtubers.length == 1 //shown only when there is only 1 youtuber
            },
        },
        plugins: {
            tooltip: {
                mode: "point",
                intersect: true,
                "rtl": true,
                "bodyAlign": "right",
                "textDirection": "rtl",
                "titleFont": { "size": 16 },
                "bodyFont": { "size": 14 },
                "callbacks": {
                    //change tooltip title to be the video title
                    "title": (tooltipItems) => {
                        const tooltipItem = tooltipItems[0];
                        if (youtubers.length === 1 && tooltipItem.datasetIndex === 1) {
                            return `${tooltipItem.dataIndex + 1} סרטונים`;
                        }
                        return flatData[tooltipItem.dataIndex].title;
                    }
                }
            },
            legend: {
                display: true,
                position: 'top',
                "rtl": true,
                "labels": {
                    "font": { "size": 16 },
                }
            },
            zoom: {
                limits: {
                    //limit the amount of scroll
                    x: {
                        "min": oldestVideoDate.getTime(),
                        "max": newestVideoDate.getTime(),
                        minRange: 1000 * 60 * 60 * 24 * 31 * 2,
                    }
                },
                pan: {
                    enabled: true,
                    mode: 'x', // Horizontal panning
                },
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true,
                    },
                    mode: 'x', // Zoom only on the x-axis                    
                }
            }
        },
        //onclick 
        onClick: (event, elements) => {
            if (elements.length > 0) {
                const el = elements[0];
                const elementIndex = el.index; // Get the clicked dot's index
                const video = flatData[elementIndex];
                const videoUrl = youtube_getVideoLink(video.id); // Construct the YouTube video link
                window.open(videoUrl, '_blank'); // Open the link in a new tab
                sendGAEvent("event", "watchVideo_fromStatsGraph", { "value": video.title })

            }
        },
        onHover: (event, chartElement) => {
            // This will change the cursor to pointer when hovering over a data point (dot)
            const canvas = event.native?.target as HTMLCanvasElement;
            if (!canvas)
                return;
            if (chartElement.length > 0) {
                canvas.style.cursor = 'pointer'; // Change cursor to pointer
            } else {
                canvas.style.cursor = 'default'; // Revert to default cursor when not hovering over a dot
            }
        },
    };
}

function CreateChartData(youtubers: YoutuberFull[], uniqueDates: Date[], youtubers_formattedData: (YoutuberFull & { videos: (Video & { viewsSum_at_point: number })[] })[]): ChartData<"line"> {
    return {
        labels: uniqueDates, // get all dates of videos as labels
        datasets: [
            ...youtubers_formattedData.map(yt =>
                GenerateDataset({
                    "label": "צפיות (" + yt.name + ")",
                    "data": GenerateDataPoints(uniqueDates, yt.videos).map((dataPoint) => dataPoint?.views || null),
                    "yAxisID": "y"
                }),
            ),

            //dynamic add / remove view sum if showing a single youtuber or many
            ...(youtubers.length == 1 && [GenerateDataset({
                "label": "סך הצפיות",
                // data: youtubers_formattedData[1].videos.map((dataPoint) => dataPoint.viewsSum_at_point),
                "data": GenerateDataPoints(uniqueDates, youtubers_formattedData[0].videos).map((dataPoint) => dataPoint?.viewsSum_at_point || null),
                "yAxisID": "y1"
            })] || [])
        ],
    }
}



/**
 * UTIL FUNCTIONS
 */

/** generate datasets point fill nulls when label is provided but no value */
function GenerateDataPoints<T extends Video>(uniqueDates: Date[], videos: T[]) {
    const videoMap = new Map(videos.map((v) => [v.createdAt.getTime(), v]));
    return uniqueDates.map((date) => videoMap.get(date.getTime()) || null);
}

/** generate Y axis def */
function GenerateAxis({ title = {} }: { title?: { [key: string]: any } }) {
    type opt = ChartOptions<"line">["scales"];
    let a: opt = {};
    return {
        title: {
            display: true,
            "font": { "weight": "bold", "size": 20 },
            ...title
        },
        grid: {
            "color": "#ffffff10"
        },
        ticks: {
            //formate date label tp match israel standard
            callback: (value) => {
                value = Number(value)
                if (value >= 1000 * 1000)
                    return (Math.round(value / 1000 / 10) / 100).toFixed(2) + "m"
                else
                    return Math.round(value / 1000).toLocaleString() + "k"
            },
        },
    } as typeof a.key
}

/** generate the config for datasets def */
function GenerateDataset({ yAxisID = "y", label, data }: { yAxisID?: string, label: string, data: any[] }) {
    return {
        label,
        data,
        tension: 0.2, // Smooth lines
        yAxisID, // Bind to primary y-axis
        pointRadius: 8, // Increased dot size
        pointHoverRadius: 10, // Larger size on hover
        spanGaps: true, // Connect lines across null values
    }
}

/** generate the config for the X axis */
function GenerateXAxisOptions(minDate: number, maxDate: number) {
    type opt = ChartOptions<"line">["scales"];
    let a: opt = {};
    return {
        type: 'time',
        time: { unit: "week" },
        min: Math.max(maxDate - 1000 * 60 * 60 * 24 * 180, minDate),
        max: maxDate,
        grid: { color: '#ffffff10' },
        ticks: {
            callback: (value: number | string) => {
                const date = new Date(value);
                return date.toLocaleDateString('he-IL', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                });
            },
        },
    } as typeof a.key;
}